import { type v2 } from '@deque/billing-service-client';
import { type TFunction } from 'i18next';

import {
  purchaseStateLabel,
  type ProductAccessRow,
  type SupportInfo
} from './useSupportInfo';

export interface SupportInfoTextParams {
  /** Sanitized URL (origin + pathname only — no query string or hash). */
  url: string;
  /** ISO timestamp captured at copy time. */
  generatedAt: string;
  /** Raw /api/billing/v2/me response (the auth context's billingUser). */
  billingUser?: v2.User;
}

const REDACTED = '[redacted]';

// Keys whose values are sensitive and redacted wherever they appear in the
// billing payload: license keys, payment-method ids, postal addresses, and the
// Stripe billing name (`stripe_name` — the user's name as held in Stripe, which
// the server adds to billing/v2/me alongside the top-level `name`). Unlike
// `name` (which doubles as the non-sensitive enterprise name), `stripe_name`
// is always personal data, so it is safe to redact by key everywhere.
const REDACTED_KEYS = new Set([
  'offline_license_key',
  'default_payment_method_id',
  'address',
  'stripe_name'
]);

function redactValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(redactValue);
  }
  if (value !== null && typeof value === 'object') {
    const entries = Object.entries(value).map(
      ([key, val]): [string, unknown] => {
        if (!REDACTED_KEYS.has(key)) {
          return [key, redactValue(val)];
        }
        return [key, val === null ? null : REDACTED];
      }
    );
    return Object.fromEntries(entries);
  }
  return value;
}

/**
 * Deep-copies the billing/v2/me payload with sensitive fields redacted:
 * license keys, payment-method ids, addresses, and the Stripe billing name
 * (`stripe_name`) anywhere in the tree, plus the top-level user `name`
 * (enterprise names are kept — they are shown in the drawer and are not
 * personal data). The payload never contains auth tokens.
 */
export const redactBillingUser = (billingUser: v2.User): unknown => {
  const redacted = redactValue(billingUser) as Record<string, unknown>;
  if (typeof redacted.name === 'string') {
    redacted.name = REDACTED;
  }
  return redacted;
};

const formatRow = (row: ProductAccessRow, t: TFunction): string =>
  `  • ${row.name} — ${purchaseStateLabel(row.state, t)}`;

/**
 * Builds the labeled, chat-friendly plain-text representation of the support
 * info for the "Copy all" action. Pure and deterministic given its inputs
 * (`url` and `generatedAt` are injected, never read from globals here).
 */
export const formatSupportInfoText = (
  info: SupportInfo,
  { url, generatedAt, billingUser }: SupportInfoTextParams,
  t: TFunction
): string => {
  const lines: string[] = [t('Axe Account — Debug Panel'), ''];

  lines.push(`${t('Keycloak ID')}: ${info.keycloakId ?? t('N/A')}`);
  lines.push(
    `${t('Active enterprise')}: ${
      info.activeEnterprise
        ? `${info.activeEnterprise.name} (${info.activeEnterprise.id})`
        : t('N/A')
    }`
  );
  lines.push(
    `${t('Enterprise admin')}: ${info.isEnterpriseAdmin ? t('Yes') : t('No')}`
  );
  lines.push('');

  const recognized = info.products.filter(row => row.recognized);
  const other = info.products.filter(row => !row.recognized);

  lines.push(`${t('Product access')}:`);
  if (recognized.length === 0) {
    lines.push(`  ${t('No product subscriptions')}`);
  } else {
    recognized.forEach(row => lines.push(formatRow(row, t)));
  }

  if (other.length > 0) {
    lines.push('');
    lines.push(`${t('Other entitlements')}:`);
    other.forEach(row => lines.push(formatRow(row, t)));
  }

  lines.push('');
  lines.push(`${t('Enterprises you belong to')} (${info.enterprises.length}):`);
  if (info.enterprises.length === 0) {
    lines.push(`  ${t('N/A')}`);
  } else {
    info.enterprises.forEach(enterprise =>
      lines.push(`  • ${enterprise.name} (${enterprise.id})`)
    );
  }

  lines.push('');
  lines.push(`${t('Generated')}: ${generatedAt}`);
  lines.push(`${t('URL')}: ${url}`);

  // billing/v2/me response for deep debugging, with sensitive fields redacted
  // (license keys, payment-method ids, addresses, and the user's name —
  // including the Stripe billing name). No auth tokens are part of this
  // payload. Appended last as it can be large.
  lines.push('');
  lines.push(`${t('Raw billing/v2/me response (redacted)')}:`);
  lines.push(
    billingUser
      ? JSON.stringify(redactBillingUser(billingUser), null, 2)
      : t('N/A')
  );

  return lines.join('\n');
};
